import {Model} from 'backbone';
import {debounce} from 'ts-debounce';
import _ from 'underscore';

import * as alignmentviewutil from 'analyses/alignment/AlignmentViewUtil';
import {Alignment} from 'analyses/Analyses';
import {assertAndContinue} from 'Asserts';
import {GAP} from 'biojs/BioConstants';
import * as util from 'biojs/Util';
import {MenuChoicesOptions} from 'components/SelectionContextMenuHelpers';
import {DNAAlignmentKind, ProteinAlignmentKind} from 'constants/AlignmentConstants';
import * as MapsConstants from 'constants/MapsConstants';
import AlignmentSelectionProps from 'molbio/alignments/backbone/AlignmentSelectionProps';
import {AlignmentRowInfoWrapperProps} from 'molbio/maps/alignment/AlignmentRowInfo';
import PolymerAlignmentRowView, {
  PolymerAlignmentRowViewInitializationOptions,
} from 'molbio/maps/alignment/PolymerAlignmentRowView';
import SequenceAlignmentTabView from 'molbio/maps/alignment/SequenceAlignmentTabView';
import {MinimalSliceView} from 'molbio/maps/linear/InfiniteScroller';
import Slice from 'molbio/maps/linear/Slice';
import {SliceView} from 'molbio/maps/linear/SliceView';
import TraceView from 'molbio/maps/linear/TraceView';
import {AlignmentMapState} from 'molbio/maps/MapStates';
import {BaseSliderProps} from 'molbio/maps/options/Sliders';
import Range from 'molbio/maps/selectables/Range';
import {Selection} from 'molbio/maps/selectables/Selection';
import {foreignObjectRenderingEnabled} from 'molbio/maps/SequenceMapConstants';
import SequenceMapUtil, {ShowInputBubbleOptions} from 'molbio/maps/SequenceMapUtil';
import SequenceAlignmentTabState from 'molbio/tabstates/SequenceAlignmentTabState';
import * as popup from 'Popup';
import * as vo from 'settings/visibility-options/VisibilityOptions';

/**
 * Debounce the slider callback so the animation isn't blocked by recalculating trace heights.
 */
const debouncedSlider = debounce((traceData: Model, value: number) => {
  traceData.set('maxY', value);
}, 20);

class SequenceAlignmentRowView
  extends PolymerAlignmentRowView<AlignmentMapState, SequenceAlignmentTabState>
  implements MinimalSliceView
{
  alignment!: Alignment;
  mapstate!: AlignmentMapState;
  slice!: Slice;
  override sliceView!: SliceView;
  tabView?: SequenceAlignmentTabView;

  constructor(
    options: PolymerAlignmentRowViewInitializationOptions<AlignmentMapState, SequenceAlignmentTabState>
  ) {
    super(options);
  }

  // PolymerAlignmentRowView assumes all subclasses will return protein-style (uppercase) alignment types
  // TODO: should not return null
  getAlignmentRowType(): ProteinAlignmentKind | null {
    switch (this.alignment.get('type')) {
      case DNAAlignmentKind.CONSENSUS:
        return MapsConstants.ALIGNMENT_TYPE.CONSENSUS;
      case DNAAlignmentKind.TEMPLATE:
        return MapsConstants.ALIGNMENT_TYPE.TEMPLATE;
      default:
        return null;
    }
  }

  isValidCharacter(char: string): boolean {
    return util.isValidBase(char, this.alignment.get('sequence').baseTable()) && char !== GAP;
  }

  showInputBubble(options?: ShowInputBubbleOptions): void {
    // @ts-expect-error: bad class hierarchy
    SequenceMapUtil.showInputBubble<AlignmentMapState>(this, {...options, refocusTo: this.$el});
  }

  storeRegionInBenchlingClipboard(e: Event): string {
    const clipboardHash = SequenceMapUtil.maybeStoreNucleotideRegionInBenchlingClipboard(
      this.getSelection(),
      e
    );
    assertAndContinue(clipboardHash != null, 'Expected a clipboard hash');
    return clipboardHash!;
  }

  handlePasteText(text: string): void {
    SequenceMapUtil.handlePasteText(this.mapstate.get('sequence'), text);
  }

  handleCtrlShiftC(e: Event): void {
    SequenceMapUtil.handleCtrlShiftC(this.mapstate.get('sequence'), e);
  }

  mimicSelection(): void {
    // Copies selection from another alignment
    const props = this.tabstate.get('selectionProps');
    const selection = this.model.get('sequence').get('selection');
    selection.set(props.pick('start', 'end', 'anchor', 'active'), {propChange: true});
  }

  propSelection(): void {
    // Propogate selection to other alignments
    const props = this.tabstate.get('selectionProps');
    const selection = this.model.get('sequence').get('selection');
    props.set(
      {
        ...selection.pick('start', 'end', 'anchor', 'active'),
        // This is only stored so it can be used when jumping back to the prior location after undo
        rowIndex: this.tabstate.get('mapstates').indexOf(this.mapstate),
      },
      {cid: this.model.cid}
    );
  }

  getSelection(): Selection {
    return this.model.get('sequence').get('selection');
  }

  getResidueSlice(start: number, end: number): string {
    return this.model.get('sequence').residueSlice(start, end);
  }

  makeSliceView(): SliceView {
    const sliceView = alignmentviewutil.makeSliceView(
      this.slice,
      this.model,
      this.tabstate,
      this.tabstate.get('range')!,
      foreignObjectRenderingEnabled()
    );
    sliceView.setElement(SliceView.createSVGElement());
    return sliceView;
  }

  getRange(): Range {
    return this.tabstate.get('range')!;
  }

  getSlice(): Slice {
    return this.slice;
  }

  undo(): void {
    if (this.tabView) {
      this.tabView.undo();
    }
  }

  override initialize(
    options: PolymerAlignmentRowViewInitializationOptions<AlignmentMapState, SequenceAlignmentTabState>
  ): void {
    super.initialize(options);
    this.tabView = options.tabView;
    this.alignment = this.model.get('alignment');
    this.mapstate = this.model; // for PolymerMapTabView
    this.slice = new Slice();
    this.slice.set(this.tabstate.get('slice')!.attributes);
    this.listenTo(this.tabstate.get('slice'), 'change', (slice: Slice): void => {
      this.slice.set(slice.attributes);
    });

    // Handle selection syncing.
    this.mimicSelection();
    this.listenTo(
      this.tabstate.get('selectionProps'),
      'change',
      (model: AlignmentSelectionProps, changeOpts: {cid: string}): void => {
        if (changeOpts.cid !== this.model.cid) {
          this.mimicSelection();
        }
      }
    );
    this.listenTo(
      this.model.get('sequence').get('selection'),
      'change',
      (model: Selection, changeOpts: {propChange?: boolean} = {}): void => {
        if (!changeOpts.propChange) {
          this.propSelection();
        }
      }
    );

    this.listenTo(this.model.get('sequence').annotations(), 'add remove reset', () => {
      this.renderRowMetadata();
    });
    this.listenTo(this.model.get('sequence').translations(), 'add remove reset', () => {
      this.renderRowMetadata();
    });
    this.listenTo(this.model.get('sequence').sequencePrimers(), 'add remove reset', () => {
      this.renderRowMetadata();
    });
    this.listenTo(this.model.get('sequence').annotations(), 'layerChange', () => {
      this.sliceView.updateComponent(MapsConstants.SliceComponentName.annotations);
    });
    this.listenTo(this.model.get('sequence').translations(), 'layerChange', () => {
      this.sliceView.updateComponent(MapsConstants.SliceComponentName.translations);
    });
  }

  height(): number {
    return this.slice.get('height')!;
  }

  deleteAlignmentRow(): void {
    this.alignment.destroy();
    this.remove();
  }

  showDeleteModal(): void {
    new popup.Confirm({
      title: 'Remove sequence?',
      msg: `<p>
  Are you sure you want to remove the sequence,
  <b>${_.escape(this.model.get('sequence').name())}</b>, from the template alignment?</p>
  <p><b>Note</b>: To re-include this sequence in the alignment, use the
  <button type='button' class='button button--small button--secondary' style='pointer-events:none;'>
    Realign
  </button> feature.
</p>`,
    })
      .choice('Remove Sequence', 'button button--small button--primary js-submit', () => {
        this.deleteAlignmentRow();
      })
      .choice('Cancel', 'button button--small button--tertiaryGray js-cancel')
      .show();
  }

  update(): void {
    this.sliceView.update();
    this.renderRowMetadata();
  }

  getRowContentProps(): AlignmentRowInfoWrapperProps {
    const alignmentType =
      this.tabstate.get('analysis')!.get('alignmentType') === DNAAlignmentKind.CONSENSUS
        ? ProteinAlignmentKind.CONSENSUS
        : ProteinAlignmentKind.TEMPLATE;
    const templateRow = this.tabstate
      .get('analysis')!
      .get('alignments')
      .find((a: Alignment) => a.get('type') === DNAAlignmentKind.TEMPLATE);
    const canEdit = this.model.get('permissions').polymerFeaturesEditable!;
    const traceData = this.alignment.get('trace');
    let sliderProps: BaseSliderProps | undefined;
    if (
      traceData != null &&
      this.sliceView.components.trace &&
      (this.sliceView.components.trace as unknown as TraceView).shouldShow()
    ) {
      sliderProps = {
        min: -traceData.getMaxTraceScore(),
        max: -25,
        value: -traceData.get('maxY'),
        onChange: (value: number) => {
          // Scale is negative so that it can go from [maxY, 25] with the orange highlight growing as the value
          // moves towards the "upper" bound of 25.
          debouncedSlider(traceData, -value);
        },
        disabled: false,
        step: 1,
      };
    }

    return {
      alignmentRowType: this.alignment.get('type'),
      canEdit,
      onDeleteRow: () => {
        this.showDeleteModal();
      },
      reserveSpaceRight: canEdit || Boolean(this.alignment.get('trace')),
      options: this.visibilityOptions,
      featureCounts: {
        annotations: this.model.get('sequence').get('annotations').getCount(),
        translations: this.model.get('sequence').get('translations').getCount(),
        sequencePrimers: this.model.get('sequence').get('sequencePrimers').getCount(),
      },
      rowInfoProps: {
        residues: this.model.get('alignment').get('sequence').get('bases'),
        trimStart: this.model.get('alignment').get('trimStart'),
        trimEnd: this.model.get('alignment').get('trimEnd'),
        visibilityOptions: this.model.get('visibilityOptions'),
        name: this.model.get('sequence').get('_sequence').get('name'),
        canEdit,
        isAaSequenceAlignment: false,
        alignmentType,
        nucleotideType: this.model.get('alignment').get('sequence').get('nucleotideType'),
        isReferenceRow: Boolean(this.getAlignmentRowType()),
        templateResidues: templateRow && templateRow.get('sequence').get('bases'),
        onNameChange: this.handleSequenceNameChange.bind(this),
        reversed: this.model.get('alignment').get('reversed'),
      },
      sliderProps,
    };
  }

  override render(): this {
    const traceData = this.alignment.get('trace');
    if (traceData != null) {
      // TODO: perform this logic as part of TraceData.initialize.
      traceData.set('maxY', traceData.getMaxTraceScore());
    }
    super.render();
    return this;
  }

  handleSequenceNameChange(name: string): void {
    this.model.get('sequence').get('_sequence').set({name});
    this.tabstate
      .get('analysis')!
      // @ts-expect-error: this save call goes through our backbone-extensions override first and eventually
      // runs the original Backbone.sync method which passes `pickSequenceFields` to `toJSON` which has is
      // overriden in AlignmentAnalysis.toJSON to handle the option.
      .save(null, {parse: false, patch: true, pickSequenceFields: ['name']})
      .done(alignmentviewutil.notifySuccess)
      .fail(alignmentviewutil.notifyError);
  }

  override registerListeners(): void {
    super.registerListeners();
    this.listenTo(this.model, 'traceToggle', () => {
      this.renderRowMetadata();
    });
    this.listenTo(this.alignment, 'updateRow', this.update.bind(this));
    this.listenTo(this.model.get('sequence').get('_sequence'), 'change:name', () => {
      this.renderRowMetadata();
    });
    this.listenTo(
      this.model.get('visibilityOptions').getOption(vo.ALIGNMENT_ROW_STATS.name),
      'change:visible',
      () => {
        this.renderRowMetadata();
      }
    );
    this.listenTo(this.model.get('alignment'), 'change:trimStart change:trimEnd', () => {
      this.renderRowMetadata();
    });
    this.listenTo(this.tabstate.get('analysis')!.getMasterSequence(), 'change:bases', () => {
      this.renderRowMetadata();
    });

    const traceData = this.alignment.get('trace');
    if (traceData != null) {
      this.listenTo(traceData, 'change:maxY', () => {
        this.renderRowMetadata();
      });
    }
  }

  override _contextMenuChoicesOptions(): MenuChoicesOptions {
    const polymer = this.model.get('sequence');
    return {
      alignment: true,
      circular: polymer.circular(),
      // Because the inheritance is a little messy, we need to smooth over the typescript errors here.
      // @ts-expect-error
      polymerMapTabView: this,
      hasTrim: polymer.get('trim') != null,
      tabstate: this.tabstate,
    };
  }
}
SequenceAlignmentRowView.initClass();

export {SequenceAlignmentRowView};
